import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  Avatar,
  Stack,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider
} from '@mui/material';
import {
  Warning as WarningIcon,
  VolumeUp as VolumeUpIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { UserCircle } from '@phosphor-icons/react';
import { SpeakerAssignment } from '@/types/speakerManagementTypes';
import { Person } from '@/types/peopleTypes';

interface SpeakerMapping {
  speakerIndex: number;
  currentSegmentText: string;
  suggestedPersonId?: string;
  confirmedPersonId?: string;
}

interface SpeakerIdentityDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (
    mappings: { speakerIndex: number; personId: string }[]
  ) => Promise<void>;
  currentSegments: Array<{
    speakerIndex: number;
    text: string;
    timestamp: string;
  }>;
  availablePeople: Person[];
  previousAssignments?: SpeakerAssignment[];
  loading?: boolean;
}

export const SpeakerIdentityDialog: React.FC<SpeakerIdentityDialogProps> = ({
  open,
  onClose,
  onConfirm,
  currentSegments,
  availablePeople,
  previousAssignments = [],
  loading = false
}) => {
  const [speakerMappings, setSpeakerMappings] = useState<SpeakerMapping[]>([]);
  const [submitting, setSubmitting] = useState(false);

  // Initialize speaker mappings when dialog opens
  useEffect(() => {
    if (open && currentSegments.length > 0) {
      const mappings: SpeakerMapping[] = currentSegments.map((segment) => {
        // Try to find previous assignment for this speaker
        const previousAssignment = previousAssignments.find(
          (assignment) => assignment.speakerIndex === segment.speakerIndex
        );

        return {
          speakerIndex: segment.speakerIndex,
          currentSegmentText:
            segment.text.substring(0, 100) +
            (segment.text.length > 100 ? '...' : ''),
          suggestedPersonId: previousAssignment?.personId,
          confirmedPersonId: previousAssignment?.personId
        };
      });

      setSpeakerMappings(mappings);
    }
  }, [open, currentSegments, previousAssignments]);

  const handlePersonSelection = (speakerIndex: number, personId: string) => {
    setSpeakerMappings((prev) =>
      prev.map((mapping) =>
        mapping.speakerIndex === speakerIndex
          ? { ...mapping, confirmedPersonId: personId }
          : mapping
      )
    );
  };

  const handleConfirm = async () => {
    try {
      setSubmitting(true);

      const confirmMappings = speakerMappings
        .filter(
          (mapping) =>
            mapping.confirmedPersonId && mapping.confirmedPersonId !== ''
        )
        .map((mapping) => ({
          speakerIndex: mapping.speakerIndex,
          personId: mapping.confirmedPersonId!
        }));

      await onConfirm(confirmMappings);
      onClose();
    } catch (error) {
      console.error('Failed to confirm speaker mappings:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = () => {
    // Continue without confirming assignments
    onClose();
  };

  const allMappingsAssigned = speakerMappings.every(
    (mapping) => mapping.confirmedPersonId && mapping.confirmedPersonId !== ''
  );

  const getPersonDisplayName = (personId: string) => {
    const person = availablePeople.find((p) => p.id === personId);
    return person ? person.displayName : 'Unknown Person';
  };

  const getPersonAvatar = (personId: string) => {
    const person = availablePeople.find((p) => p.id === personId);
    if (person?.avatarUrl) {
      return <Avatar src={person.avatarUrl} sx={{ width: 32, height: 32 }} />;
    }
    if (person) {
      return (
        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
          {person.displayName
            .split(' ')
            .map((n) => n[0])
            .join('')
            .toUpperCase()}
        </Avatar>
      );
    }
    return (
      <Avatar sx={{ width: 32, height: 32, bgcolor: 'grey.400' }}>
        <UserCircle size={20} weight="fill" />
      </Avatar>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={!loading && !submitting ? onClose : undefined}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={loading || submitting}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <WarningIcon color="warning" />
          <Typography variant="h6">Confirm Speaker Identities</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Your session was reconnected. Please confirm who is currently speaking
          to maintain accurate speaker tracking.
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Alert severity="info" sx={{ mb: 3 }}>
          We detected {speakerMappings.length} speaker(s) in the current
          conversation. Please confirm the identity of each speaker to continue
          accurate transcription.
        </Alert>

        <Stack spacing={3}>
          {speakerMappings.map((mapping) => (
            <Card key={mapping.speakerIndex} variant="outlined">
              <CardContent>
                <Box display="flex" alignItems="flex-start" gap={2}>
                  {/* Speaker Indicator */}
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    minWidth={80}
                  >
                    <Chip
                      icon={<VolumeUpIcon />}
                      label={`Speaker ${mapping.speakerIndex}`}
                      color="primary"
                      variant="outlined"
                      size="small"
                    />
                    {mapping.suggestedPersonId && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        align="center"
                        sx={{ mt: 1 }}
                      >
                        Previously:{' '}
                        {getPersonDisplayName(mapping.suggestedPersonId)}
                      </Typography>
                    )}
                  </Box>

                  {/* Content */}
                  <Box flexGrow={1}>
                    <Typography variant="subtitle2" gutterBottom>
                      Recent Speech Sample:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontStyle: 'italic',
                        bgcolor: 'grey.50',
                        p: 1.5,
                        borderRadius: 1,
                        mb: 2
                      }}
                    >
                      "{mapping.currentSegmentText}"
                    </Typography>

                    {/* Person Selection */}
                    <FormControl fullWidth>
                      <InputLabel>Assign to Person</InputLabel>
                      <Select
                        value={mapping.confirmedPersonId || ''}
                        onChange={(e) =>
                          handlePersonSelection(
                            mapping.speakerIndex,
                            e.target.value
                          )
                        }
                        label="Assign to Person"
                        disabled={loading || submitting}
                      >
                        <MenuItem value="">
                          <Box display="flex" alignItems="center" gap={1}>
                            <UserCircle size={20} />
                            <Typography color="text.secondary">
                              Unassigned
                            </Typography>
                          </Box>
                        </MenuItem>

                        {mapping.suggestedPersonId && (
                          <MenuItem value={mapping.suggestedPersonId}>
                            <Box display="flex" alignItems="center" gap={1}>
                              {getPersonAvatar(mapping.suggestedPersonId)}
                              <Box>
                                <Typography>
                                  {getPersonDisplayName(
                                    mapping.suggestedPersonId
                                  )}
                                </Typography>
                                <Typography variant="caption" color="primary">
                                  (Previous assignment)
                                </Typography>
                              </Box>
                            </Box>
                          </MenuItem>
                        )}

                        <Divider />

                        {availablePeople
                          .filter(
                            (person) => person.id !== mapping.suggestedPersonId
                          )
                          .map((person) => (
                            <MenuItem key={person.id} value={person.id}>
                              <Box display="flex" alignItems="center" gap={1}>
                                {getPersonAvatar(person.id)}
                                <Box>
                                  <Typography>{person.displayName}</Typography>
                                  {person.email && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      {person.email}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>

        {speakerMappings.length === 0 && (
          <Alert severity="warning">
            No current speakers detected. You can continue without making
            assignments.
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button
          onClick={handleSkip}
          disabled={loading || submitting}
          color="inherit"
        >
          Skip for Now
        </Button>

        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={loading || submitting || speakerMappings.length === 0}
          startIcon={submitting ? undefined : <PersonIcon />}
        >
          {submitting
            ? 'Updating...'
            : allMappingsAssigned
            ? 'Confirm All Assignments'
            : 'Confirm Selected'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SpeakerIdentityDialog;
